import { folderAudioSetAside } from './folder-audio-receipt';
import { createSelectedFileScope, fileMatchesSelectedScope } from './selected-file-scope.js';
import { isExplicitReferenceOnlyVideo, VIDEO_REFERENCE_ONLY_DURABILITY_LIMITATION } from './video-storage-policy';
import { selectRequiredProjectPhotos } from '../utils/project-photo-contract';

export interface IntakeReadinessIssue {
    kind: string;
    id: string | null;
    name: string;
    reason: string;
}

export interface IntakeReadinessCounts {
    documents: number;
    photos: number;
    videos: number;
}

export interface ProjectIntakeReadiness {
    ready: boolean;
    issues: IntakeReadinessIssue[];
    deferred: IntakeReadinessIssue[];
    counts: IntakeReadinessCounts;
}

export interface EvaluateReadinessOptions {
    selectedFileIds?: string[] | Set<string> | null;
}

export interface HasDurableIntakeOptions {
    backendDisabled?: boolean;
}

function hasText(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0;
}

function hasDataUrl(value: unknown): boolean {
    return typeof value === 'string' && value.startsWith('data:');
}

function hasBinaryBytes(value: unknown): boolean {
    if (typeof Blob !== 'undefined' && value instanceof Blob) return value.size > 0;
    if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) return value.byteLength > 0;
    return typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(value) && value.byteLength > 0;
}

function hasUsableDocument(document: Record<string, unknown> | null | undefined): boolean {
    return hasText(document?.text)
        || hasText(document?.content)
        || hasText(document?.extractedText)
        || hasText(document?.ocrText);
}

function hasUsablePhoto(photo: Record<string, unknown> | null | undefined): boolean {
    return !!photo?.blobId
        || !!photo?.s3Key
        || !!photo?.fileMetadataId
        || hasDataUrl(photo?.data)
        || hasDataUrl(photo?.dataUrl)
        || (typeof photo?.url === 'string' && photo.url.trim().length > 0);
}

function hasMatchingTranscript(video: Record<string, unknown> | null | undefined, transcripts: unknown): boolean {
    const videoIds = new Set([
        video?.id,
        video?.videoId,
        video?.quoteMakerFileId,
    ].filter(Boolean).map(String));
    const videoNames = new Set([
        video?.name,
        video?.originalName,
        video?.fileName,
    ].filter(Boolean).map(value => String(value).toLowerCase()));
    return (Array.isArray(transcripts) ? transcripts : []).some((transcript) => {
        const transcriptId = transcript?.videoId ?? transcript?.id ?? transcript?.quoteMakerFileId;
        const transcriptName = transcript?.fileName ?? transcript?.name ?? transcript?.originalName;
        const sameVideo = (transcriptId != null && videoIds.has(String(transcriptId)))
            || (transcriptName != null && videoNames.has(String(transcriptName).toLowerCase()));
        return sameVideo && (
            hasText(transcript?.fullText)
            || hasText(transcript?.text)
            || hasText(transcript?.transcript)
        );
    });
}

function hasUsableVideo(video: Record<string, unknown> | null | undefined, transcripts: unknown = []): boolean {
    const hasSource = !!video?.videoBlobId
        || !!video?.s3Key
        || !!video?.fileMetadataId
        || (typeof video?.url === 'string' && video.url.trim().length > 0);
    if (!hasSource) return false;
    return hasText(video?.transcript)
        || (Array.isArray(video?.workerKeyframes) && video.workerKeyframes.length > 0)
        || hasMatchingTranscript(video, transcripts);
}

function scoped<T>(items: unknown, scope: unknown): T[] {
    const rows = (Array.isArray(items) ? items : []) as T[];
    return scope ? rows.filter(item => fileMatchesSelectedScope(item, scope)) : rows;
}

function issue(kind: string, item: Record<string, unknown> | null | undefined, reason: string): IntakeReadinessIssue {
    return {
        kind,
        id: (item?.id as string) || null,
        name: (item?.name as string) || (item?.originalName as string) || `${kind} item`,
        reason,
    };
}

// 2026-07-29 (batch-quoting-first-run, operator directive): a per-file gap
// must never strand the whole project. Intake blocks only on a stale
// selection or when the ENTIRE scope carries nothing an AI draft could use.
// Everything else (a video awaiting transcript/keyframes, a document whose
// text could not be extracted, and, since 2026-10-23, a photo or video whose
// bytes were not stored or that a run set aside as unreadable) is DEFERRED:
// carried into the draft run, recorded visibly on the readiness result, never
// silently excluded and never fatal. Peter, 2026-10-22, for Full AI: "If photos
// or other uploaded files are corrupt or 0 bytes or whatever, just ignore those
// files and continue if you can"; 2026-10-23: the Quote Run carries on and
// flags as Full AI does.
function hasDurableVideoSource(video: Record<string, unknown> | null | undefined): boolean {
    // A runner that tried to read the bytes and found them gone knows better
    // than field presence — a dangling videoBlobId must not read as durable.
    if (video?.sourceMissing === true) return false;
    // blob: object URLs are session-scoped; a persisted one is a dead pointer,
    // not a source (data-service cleanup strips them for the same reason).
    const url = typeof video?.url === 'string' ? video.url.trim() : '';
    const durableUrl = url.length > 0 && !url.startsWith('blob:');
    return !!video?.videoBlobId
        || !!video?.s3Key
        || !!video?.fileMetadataId
        || durableUrl;
}

function recordedVideoReason(video: Record<string, unknown>): string {
    const warnings = Array.isArray(video?.warnings) ? video.warnings : [];
    const recorded = String(warnings.at(-1) || '').trim();
    return recorded || 'The video could not be read, so it was set aside.';
}

/** Photos a run set aside as unreadable (photo-coverage-discharge), by id. */
function photosSetAsideAsUnreadable(project: Record<string, unknown> | null | undefined): Set<string> {
    const analysis = (project?.photoAnalysis ?? project?.photo_analysis) as Record<string, unknown> | null | undefined;
    const acknowledgement = analysis?.unreadable_acknowledgement as Record<string, unknown> | null | undefined;
    const ids = Array.isArray(acknowledgement?.photo_ids) ? acknowledgement.photo_ids : [];
    return new Set(ids.filter(Boolean).map(String));
}

export function evaluateProjectIntakeReadiness(project: Record<string, unknown> | null | undefined, { selectedFileIds = null }: EvaluateReadinessOptions = {}): ProjectIntakeReadiness {
    const scope = createSelectedFileScope(selectedFileIds);
    const allDocuments = Array.isArray(project?.documents) ? project.documents : [];
    const allPhotos = selectRequiredProjectPhotos(project?.photos);
    const allVideos = Array.isArray(project?.videos) ? project.videos : [];
    const videoTranscripts = Array.isArray(project?.videoTranscripts) ? project.videoTranscripts : [];
    const documents = scoped<Record<string, unknown>>(allDocuments, scope);
    const photos = scoped<Record<string, unknown>>(allPhotos, scope);
    const videos = scoped<Record<string, unknown>>(allVideos, scope);
    const issues: IntakeReadinessIssue[] = [];
    const deferred: IntakeReadinessIssue[] = [];

    if (scope && documents.length + photos.length + videos.length === 0) {
        issues.push({
            kind: 'selection',
            id: null,
            name: 'Selected files',
            reason: 'The queued file selection no longer matches this project. Re-open the import selection and queue it again.',
        });
    }
    let usableDocuments = 0;
    for (const document of documents) {
        const preparation = document?.evidencePreparation as Record<string, unknown> | null | undefined;
        const setAside = preparation?.status === 'set-aside';
        if (hasUsableDocument(document)) {
            usableDocuments += 1;
            // Its imported text still informs the draft; the rest of the file was not read.
            if (setAside) deferred.push(issue('document', document, String(preparation?.reason || 'The file could not be read further, so it was set aside.')));
        } else {
            deferred.push(issue('document', document, setAside && preparation?.reason
                ? String(preparation.reason)
                : 'No text could be extracted, so this file cannot inform the AI draft. It stays attached for human review.'));
        }
    }
    const unreadablePhotoIds = photosSetAsideAsUnreadable(project);
    let usablePhotos = 0;
    for (const photo of photos) {
        if (photo?.id != null && unreadablePhotoIds.has(String(photo.id))) {
            deferred.push(issue('photo', photo, 'The photo could not be read, so it was set aside.'));
        } else if (hasUsablePhoto(photo)) {
            usablePhotos += 1;
        } else {
            deferred.push(issue('photo', photo, 'The photo was not stored, so it was set aside. Import it again to use it.'));
        }
    }
    let usableVideos = 0;
    for (const video of videos) {
        if (hasUsableVideo(video, videoTranscripts)) {
            usableVideos += 1;
        } else if (isExplicitReferenceOnlyVideo(video)) {
            deferred.push(issue('video', video, `Local/reference-only video has no AI evidence. ${VIDEO_REFERENCE_ONLY_DURABILITY_LIMITATION}`));
        } else if (video?.importRejected === true || video?.evidenceDeferred === true) {
            // Over the import limit (preprocessed-row-builder), or set aside by a run.
            deferred.push(issue('video', video, recordedVideoReason(video)));
        } else if (hasDurableVideoSource(video)) {
            deferred.push(issue('video', video, 'No transcript or keyframes yet. The footage rides along for human review; process it in Stage 1 if the draft needs video evidence.'));
        } else {
            deferred.push(issue('video', video, 'The video was not stored, so it was set aside. Import it again to use it.'));
        }
    }
    // A folder recording has no row (a Stage 1 drop keeps none): the run's
    // record on the form names the ones it set aside.
    for (const recording of folderAudioSetAside(project as { formData?: Record<string, unknown> } | null)) {
        if (!scope || fileMatchesSelectedScope(recording, scope)) deferred.push(issue('audio', recording, recording.reason));
    }
    // A project whose whole scope carries nothing an AI draft could read is a
    // genuine blocker — running Stages 1-5 against it would only spend money
    // on an empty prompt.
    if (issues.length === 0
        && usableDocuments === 0
        && usablePhotos === 0
        && usableVideos === 0) {
        issues.push({
            kind: 'content',
            id: null,
            name: 'Imported files',
            reason: 'None of the imported files produced AI-usable content (no document text, no photos, no processed video). Add at least one readable RFQ document or photo.',
        });
    }

    return {
        ready: issues.length === 0,
        issues,
        deferred,
        counts: {
            documents: documents.length,
            photos: photos.length,
            videos: videos.length,
        },
    };
}

export function assertProjectIntakeReady(project: Record<string, unknown> | null | undefined, options: EvaluateReadinessOptions = {}): ProjectIntakeReadiness {
    const readiness = evaluateProjectIntakeReadiness(project, options);
    if (readiness.ready) return readiness;
    const first = readiness.issues[0];
    const error: any = new Error(
        `Project media is not ready for AI: ${first?.name || 'an imported file'} — ${first?.reason || 'finish importing first'}`,
    );
    error.code = first?.kind === 'selection' ? 'SELECTED_FILE_SCOPE_MISMATCH' : 'PROJECT_MEDIA_NOT_READY';
    error.category = 'intake-readiness';
    error.retryable = true;
    error.operatorAction = 'Resolve the listed import/media issue, then retry this queued project. No AI task was started.';
    error.details = readiness;
    throw error;
}

export const _internals = {
    hasBinaryBytes,
    hasUsableDocument,
    hasUsablePhoto,
    hasUsableVideo,
    hasMatchingTranscript,
};

// Shared proof gate for recovering the durable boundary between deterministic
// intake and prepare-quote. A completed queue row alone is only audit history;
// promotion is actionable when the matching project also proves that intake
// reached ready_for_analysis under the configured backend mode.

export function hasDurableIntakeReadyProof(project: Record<string, unknown> | null | undefined, projectId: unknown, { backendDisabled = false }: HasDurableIntakeOptions = {}): boolean {
    const expectedId = String(projectId || '').trim();
    const hasBackendProof = project?.backendVerificationMode === 'backend'
        && String(project?.backendVerifiedProjectId || '').trim() === expectedId
        && typeof project?.backendVerifiedAt === 'string'
        && project.backendVerifiedAt.trim().length > 0;
    const hasDisabledBackendProof = backendDisabled
        && project?.backendVerificationMode === 'disabled';
    return project?.status === 'ready_for_analysis'
        && (hasBackendProof || hasDisabledBackendProof);
}
